import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  FolderOpen,
  GitBranch,
  Globe,
  Loader2,
  Maximize2,
  Minimize2,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SourcesPanel } from "@/components/chat/SourcesPanel";
import { Markdown } from "@/components/shared/Markdown";
import { taskService } from "@/lib/services";
import type { ChildTaskSummary, SourceMetadata, Task, TaskStep, TaskStepStatus } from "@/types";

// -- helpers ----------------------------------------------------------------

function childStatusLabel(status: string): { label: string; color: string } {
  switch (status) {
    case "completed":
      return { label: "Completed", color: "text-green-400" };
    case "failed":
      return { label: "Failed", color: "text-red-400" };
    case "running":
      return { label: "Running", color: "text-blue-400" };
    case "planning":
      return { label: "Planning", color: "text-blue-400" };
    case "waiting_for_input":
      return { label: "Waiting", color: "text-yellow-400" };
    case "cancelled":
      return { label: "Cancelled", color: "text-content-tertiary" };
    default:
      return { label: status, color: "text-content-tertiary" };
  }
}

function ChildStepStatusIcon({ status }: Readonly<{ status: TaskStepStatus }>) {
  switch (status) {
    case "running":
      return <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin" />;
    case "completed":
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />;
    case "failed":
      return <XCircle className="h-3.5 w-3.5 text-red-400" />;
    default:
      return <Clock className="h-3.5 w-3.5 text-content-quaternary" />;
  }
}

function childStepBorder(status: TaskStepStatus): string {
  switch (status) {
    case "running":
      return "border-blue-500/20 bg-blue-500/5";
    case "completed":
      return "border-green-500/20 bg-green-500/5";
    case "failed":
      return "border-red-500/20 bg-red-500/5";
    default:
      return "border-border-secondary bg-surface-secondary/30";
  }
}

// -- source tree helpers ----------------------------------------------------

/** Icon and color config per source type (matches SourcesPanel). */
const SOURCE_ICON: Record<string, { icon: typeof FileText; color: string }> = {
  rag: { icon: FileText, color: "text-green-400" },
  web: { icon: Globe, color: "text-blue-400" },
  sharepoint: { icon: FolderOpen, color: "text-purple-400" },
};

function getSourceIcon(type: string) {
  return SOURCE_ICON[type] ?? SOURCE_ICON.rag;
}

/** Group sources by the child step that produced them. */
interface SourceGroup {
  stepTitle: string;
  stepOrder: number;
  sources: SourceMetadata[];
}

function buildSourceTree(childTask: Task): SourceGroup[] {
  const groups: SourceGroup[] = [];
  if (!childTask.steps) return groups;

  for (const step of childTask.steps) {
    if (step.sources && step.sources.length > 0) {
      groups.push({
        stepTitle: step.title,
        stepOrder: step.stepOrder,
        sources: step.sources,
      });
    }
  }
  return groups;
}

/**
 * Collapsible source tree that groups sources by the child step that produced them.
 * Sits below the delegated step's main content.
 */
function DelegatedSourceTree({
  childTask,
  parentStepSources,
}: Readonly<{
  childTask: Task;
  parentStepSources?: SourceMetadata[];
}>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const groups = useMemo(() => buildSourceTree(childTask), [childTask]);

  // Total source count across all child steps
  const totalSources = groups.reduce((acc, g) => acc + g.sources.length, 0);
  // Also count parent-level sources that didn't come from child (usually 0 for pre-planned delegation)
  const extraParentSources = useMemo(() => {
    if (!parentStepSources || parentStepSources.length === 0 || totalSources === 0) return [];
    // Sources on the parent step that are NOT in any child step
    // Simple heuristic: parent step sources for delegated steps are child-aggregated,
    // so usually all match. Return empty if parent count <= total child count.
    if (parentStepSources.length <= totalSources) return [];
    return parentStepSources;
  }, [parentStepSources, totalSources]);

  if (totalSources === 0 && extraParentSources.length === 0) return null;

  return (
    <div className="rounded-lg border border-border-secondary bg-surface-secondary">
      <button
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-content-tertiary hover:text-content-secondary transition cursor-pointer"
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="font-medium">
          {totalSources} source{totalSources === 1 ? "" : "s"} from sub-agent
          {groups.length > 1 && ` (${groups.length} steps)`}
        </span>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-2">
          {groups.map((group) => (
            <SourceTreeGroup key={group.stepOrder} group={group} />
          ))}
          {extraParentSources.length > 0 && (
            <div className="mt-2">
              <SourcesPanel sources={extraParentSources} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SourceTreeGroup({ group }: Readonly<{ group: SourceGroup }>) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-sm border border-border-secondary/60 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[11px] text-content-secondary hover:bg-surface-tertiary/30 transition cursor-pointer"
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-content-quaternary" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-content-quaternary" />
        )}
        <span className="font-medium truncate flex-1 min-w-0">
          Step {group.stepOrder + 1}: {group.stepTitle}
        </span>
        <span className="text-[10px] text-content-quaternary shrink-0">
          {group.sources.length} source{group.sources.length === 1 ? "" : "s"}
        </span>
      </button>

      {open && (
        <div className="px-2.5 pb-2 pt-0.5 space-y-1">
          {group.sources.map((src, i) => {
            const { icon: Icon, color } = getSourceIcon(src.type);
            return (
              <div
                key={`${src.type}-${src.name}-${i}`}
                className="flex items-center gap-2 px-2 py-1 rounded-sm bg-surface-secondary/50"
              >
                <Icon className={`h-3 w-3 shrink-0 ${color}`} />
                {src.url ? (
                  <a
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-[11px] ${color} hover:underline truncate`}
                    title={src.name}
                  >
                    {src.name}
                  </a>
                ) : (
                  <span className="text-[11px] text-content-secondary truncate" title={src.name}>
                    {src.name}
                  </span>
                )}
                {src.score != null && (
                  <span className="text-[9px] text-content-quaternary shrink-0 ml-auto">
                    {Math.round(src.score * 100)}%
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -- props ------------------------------------------------------------------

interface DelegatedStepDetailProps {
  /** UUID of the child (delegated) task. */
  childTaskUuid: string;
  /** Quick summary from the parent task's children array (may be undefined during streaming). */
  childSummary?: ChildTaskSummary;
  /** Step order in the parent task (for labeling). */
  parentStepOrder: number;
  /** Sources from the parent step (to show in the source tree). */
  parentStepSources?: SourceMetadata[];
}

// -- component --------------------------------------------------------------

/**
 * Renders an inline view of a delegated (child) task within its parent step.
 *
 * Collapsed: shows agent name, status badge, and a short summary line.
 * Expanded: fetches the full child task and displays its steps with outputs.
 */
export function DelegatedStepDetail({
  childTaskUuid,
  childSummary,
  parentStepOrder: _parentStepOrder,
  parentStepSources,
}: Readonly<DelegatedStepDetailProps>) {
  const [expanded, setExpanded] = useState(false);
  const [childTask, setChildTask] = useState<Task | null>(null);
  const [loadingChild, setLoadingChild] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedChildSteps, setExpandedChildSteps] = useState<Set<number>>(new Set());
  const [fullOutputChildSteps, setFullOutputChildSteps] = useState<Set<number>>(new Set());

  // Fetch full child task when expanded for the first time
  const fetchChild = useCallback(async () => {
    if (childTask || loadingChild) return;
    setLoadingChild(true);
    setLoadError(null);
    try {
      const resp = await taskService.get(childTaskUuid);
      setChildTask(resp.data);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load delegated task");
    } finally {
      setLoadingChild(false);
    }
  }, [childTaskUuid, childTask, loadingChild]);

  useEffect(() => {
    if (expanded && !childTask && !loadingChild) {
      fetchChild();
    }
  }, [expanded, childTask, loadingChild, fetchChild]);

  const toggleChildStep = (order: number) => {
    setExpandedChildSteps((prev) => {
      const next = new Set(prev);
      if (next.has(order)) next.delete(order);
      else next.add(order);
      return next;
    });
  };

  // Derive display values
  const agentName = childTask?.agentName ?? childSummary?.agentName ?? "Sub-agent";
  const status = childTask?.status ?? childSummary?.status ?? "running";
  const { label: statusLabel, color: statusColor } = childStatusLabel(status);
  const totalTokens = childTask?.totalTokens ?? childSummary?.totalTokens ?? 0;
  const stepCount = childTask?.steps?.length ?? childSummary?.stepCount ?? 0;
  const finalOutput = childTask?.finalOutput ?? childSummary?.finalOutput;

  return (
    <div className="mt-2 rounded-lg border border-purple-500/20 bg-purple-500/5 overflow-hidden">
      {/* Collapsed header */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-purple-500/10 transition-colors"
      >
        <GitBranch className="h-4 w-4 text-purple-400 shrink-0" />
        <span className="text-sm font-medium text-content-primary truncate">Delegated to {agentName}</span>
        <span className={`text-xs font-medium ${statusColor} shrink-0`}>{statusLabel}</span>
        {stepCount > 0 && (
          <span className="text-xs text-content-tertiary shrink-0">
            {stepCount} step{stepCount === 1 ? "" : "s"}
          </span>
        )}
        {totalTokens > 0 && (
          <span className="text-xs text-content-quaternary shrink-0">{totalTokens.toLocaleString()} tokens</span>
        )}
        <span className="flex-1" />
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-content-tertiary shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-content-tertiary shrink-0" />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-purple-500/20 px-4 py-3 space-y-3">
          {loadingChild && (
            <div className="flex items-center gap-2 text-sm text-content-tertiary py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading delegated task details...
            </div>
          )}

          {loadError && (
            <div className="flex items-center gap-2 text-sm text-red-400 py-2">
              <AlertCircle className="h-4 w-4" />
              {loadError}
              <button
                onClick={() => {
                  setChildTask(null);
                  setLoadError(null);
                  fetchChild();
                }}
                className="ml-2 text-xs underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Child task goal */}
          {childTask && (
            <div className="text-xs text-content-tertiary">
              <span className="font-medium text-content-secondary">Goal: </span>
              {childTask.goal.length > 200 ? `${childTask.goal.slice(0, 200)}...` : childTask.goal}
            </div>
          )}

          {/* Child steps */}
          {childTask?.steps && childTask.steps.length > 0 && (
            <div className="space-y-2">
              {childTask.steps.map((step: TaskStep) => {
                const isStepExpanded = expandedChildSteps.has(step.stepOrder);
                const output = step.output ?? "";
                const hasContent = !!(output || step.errorMessage);

                return (
                  <div
                    key={step.stepOrder}
                    className={`rounded-sm border transition-colors ${childStepBorder(step.status)}`}
                  >
                    <button
                      onClick={() => hasContent && toggleChildStep(step.stepOrder)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm ${
                        hasContent ? "cursor-pointer" : "cursor-default"
                      }`}
                    >
                      <div className="flex items-center justify-center h-5 w-5 rounded-full bg-surface-tertiary/50 text-[10px] font-medium text-content-tertiary shrink-0">
                        {step.stepOrder + 1}
                      </div>
                      <span className="flex-1 min-w-0 truncate text-content-primary">{step.title}</span>
                      {step.tokensUsed > 0 && (
                        <span className="text-[10px] text-content-quaternary shrink-0">
                          {step.tokensUsed.toLocaleString()}
                        </span>
                      )}
                      <ChildStepStatusIcon status={step.status} />
                      {hasContent &&
                        (isStepExpanded ? (
                          <ChevronDown className="h-3 w-3 text-content-tertiary shrink-0" />
                        ) : (
                          <ChevronRight className="h-3 w-3 text-content-tertiary shrink-0" />
                        ))}
                    </button>

                    {isStepExpanded && hasContent && (
                      <div className="px-3 pb-3 pt-0 space-y-2">
                        {step.errorMessage && (
                          <div className="p-2 bg-red-500/10 border border-red-500/20 rounded-sm text-xs text-red-300">
                            {step.errorMessage}
                          </div>
                        )}
                        {output &&
                          (() => {
                            const hasSummary = !!step.summary && step.summary !== output;
                            const showFull = fullOutputChildSteps.has(step.stepOrder);
                            const displayContent = hasSummary && !showFull ? step.summary! : output;
                            return (
                              <>
                                {hasSummary && (
                                  <div className="flex items-center justify-end">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFullOutputChildSteps((prev) => {
                                          // NOSONAR — state updater toggling Set membership; complexity inherent to toggle pattern
                                          const next = new Set(prev);
                                          if (next.has(step.stepOrder)) next.delete(step.stepOrder);
                                          else next.add(step.stepOrder);
                                          return next;
                                        });
                                      }}
                                      className="flex items-center gap-1 text-[10px] text-content-tertiary hover:text-content-secondary transition-colors px-1.5 py-0.5 rounded-sm hover:bg-surface-tertiary/30"
                                    >
                                      {showFull ? (
                                        <>
                                          <Minimize2 className="h-2.5 w-2.5" />
                                          Summary
                                        </>
                                      ) : (
                                        <>
                                          <Maximize2 className="h-2.5 w-2.5" />
                                          Full
                                        </>
                                      )}
                                    </button>
                                  </div>
                                )}
                                <div className="p-2 bg-surface-secondary/50 rounded-sm text-xs max-h-60 overflow-auto">
                                  <Markdown content={displayContent} />
                                </div>
                              </>
                            );
                          })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Source tree — grouped by child step */}
          {childTask && <DelegatedSourceTree childTask={childTask} parentStepSources={parentStepSources} />}

          {/* Child final output (truncated preview) */}
          {finalOutput && (
            <div className="border-t border-purple-500/10 pt-2">
              <div className="text-xs font-medium text-content-secondary mb-1">Sub-agent Output</div>
              <div className="p-2 bg-surface-secondary/50 rounded-sm text-xs max-h-40 overflow-auto">
                <Markdown content={finalOutput} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
